import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Upload, Trash2, Search, Image, Copy, Check, X, RefreshCw, CheckSquare, Square, AlertCircle } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MediaGallery = () => {
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'in_use', 'unused'
  const [stats, setStats] = useState({ total: 0, in_use: 0, safe_to_delete: 0 });

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/uploads/images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data.images || []);
        setStats({
          total: data.total || 0,
          in_use: data.in_use || 0,
          safe_to_delete: data.safe_to_delete || 0
        });
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      toast({
        title: 'Error',
        description: 'Failed to load images',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const token = localStorage.getItem('token');
    let successCount = 0;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API_URL}/api/uploads/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          successCount++;
        } else {
          let errorMessage = `Failed to upload ${file.name}`;
          try {
            const error = await res.json();
            errorMessage = error.detail || errorMessage;
          } catch (e) {
            // Response body might already be consumed or not JSON
          }
          toast({
            title: 'Upload Failed',
            description: errorMessage,
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast({
          title: 'Upload Error',
          description: 'Failed to upload image. Please check your connection.',
          variant: 'destructive'
        });
      }
    }

    if (successCount > 0) {
      toast({
        title: 'Upload Complete',
        description: `Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}`
      });
      fetchImages();
    }
    setUploading(false);
  };

  const handleDelete = async (filename, isInUse = false) => {
    // Show stronger warning for in-use images
    const confirmMsg = isInUse 
      ? `⚠️ WARNING: "${filename}" is currently IN USE on the website!\n\nDeleting it will cause broken images on the live site.\n\nAre you absolutely sure you want to delete it?`
      : `Delete "${filename}"? This cannot be undone.`;
    
    if (!window.confirm(confirmMsg)) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/uploads/image/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast({ title: 'Deleted', description: 'Image removed from gallery' });
        fetchImages();
        if (selectedImage?.filename === filename) {
          setSelectedImage(null);
        }
      } else {
        toast({ title: 'Error', description: 'Failed to delete image', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const toggleSelectImage = (filename) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(filename)) {
      newSelected.delete(filename);
    } else {
      newSelected.add(filename);
    }
    setSelectedImages(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;
    
    // Check if any selected images are in use
    const inUseSelected = Array.from(selectedImages).filter(filename => 
      images.find(img => img.filename === filename)?.in_use
    );
    
    let confirmMsg = `Delete ${selectedImages.size} selected images? This cannot be undone.`;
    if (inUseSelected.length > 0) {
      confirmMsg = `⚠️ WARNING: ${inUseSelected.length} of the ${selectedImages.size} selected images are IN USE on the website!\n\nDeleting them will cause broken images on the live site.\n\nAre you absolutely sure you want to continue?`;
    }
    
    if (!window.confirm(confirmMsg)) return;
    
    setDeleting(true);
    const token = localStorage.getItem('token');
    let successCount = 0;
    let failCount = 0;
    
    for (const filename of selectedImages) {
      try {
        const res = await fetch(`${API_URL}/api/uploads/image/${encodeURIComponent(filename)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }
    
    setDeleting(false);
    setSelectedImages(new Set());
    setSelectMode(false);
    fetchImages();
    
    if (successCount > 0) {
      toast({ 
        title: 'Deleted', 
        description: `Removed ${successCount} image${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`
      });
    }
  };

  const selectAll = () => {
    setSelectedImages(new Set(filteredImages.map(img => img.filename)));
  };

  const deselectAll = () => {
    setSelectedImages(new Set());
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
    toast({ title: 'Copied!', description: 'Image URL copied to clipboard' });
  };

  const isVideo = (filename = '') => /\.(mp4|mov|webm)$/i.test(filename);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      handleUpload(files);
    }
  }, []);

  // Filter images by search term AND filter mode (in_use status)
  const filteredImages = images.filter(img => {
    // First apply search filter
    const matchesSearch = img.filename.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    
    // Then apply in_use filter
    if (filterMode === 'in_use') return img.in_use === true;
    if (filterMode === 'unused') return img.in_use === false;
    return true; // 'all' mode
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black">Media Gallery</h1>
          <p className="text-gray-600">Upload and manage images for use across the CMS</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={fetchImages} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            onClick={() => {
              setSelectMode(!selectMode);
              if (selectMode) {
                setSelectedImages(new Set());
              }
            }} 
            variant={selectMode ? "default" : "outline"} 
            size="sm"
            className={selectMode ? "bg-purple-500 hover:bg-purple-600" : ""}
          >
            <CheckSquare className="w-4 h-4 mr-1" />
            {selectMode ? 'Done' : 'Select'}
          </Button>
        </div>
      </div>

      {/* Stats & Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border">
        <div className="flex items-center gap-4 text-sm">
          <span className="font-medium">{stats.total} images</span>
          <span className="text-green-600">✓ {stats.in_use} in use</span>
          <span className="text-orange-600">○ {stats.safe_to_delete} unused</span>
        </div>
        <div className="flex-1" />
        <div className="flex gap-1">
          <Button 
            onClick={() => setFilterMode('all')} 
            variant={filterMode === 'all' ? 'default' : 'outline'} 
            size="sm"
            className={filterMode === 'all' ? 'bg-gray-700' : ''}
          >
            All
          </Button>
          <Button 
            onClick={() => setFilterMode('in_use')} 
            variant={filterMode === 'in_use' ? 'default' : 'outline'} 
            size="sm"
            className={filterMode === 'in_use' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            In Use
          </Button>
          <Button 
            onClick={() => setFilterMode('unused')} 
            variant={filterMode === 'unused' ? 'default' : 'outline'} 
            size="sm"
            className={filterMode === 'unused' ? 'bg-orange-500 hover:bg-orange-600' : ''}
          >
            Unused
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
          <span className="text-sm font-medium text-purple-700">
            {selectedImages.size} selected
          </span>
          <div className="flex-1" />
          <Button onClick={selectAll} variant="outline" size="sm">
            Select All
          </Button>
          <Button 
            onClick={() => {
              // Select only unused images
              const unusedImages = filteredImages.filter(img => !img.in_use).map(img => img.filename);
              setSelectedImages(new Set(unusedImages));
            }} 
            variant="outline" 
            size="sm"
          >
            Select Unused Only
          </Button>
          <Button onClick={deselectAll} variant="outline" size="sm">
            Deselect All
          </Button>
          <Button 
            onClick={handleBulkDelete} 
            variant="destructive" 
            size="sm"
            disabled={selectedImages.size === 0 || deleting}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {deleting ? 'Deleting...' : `Delete (${selectedImages.size})`}
          </Button>
          {/* Warning if any selected images are in use */}
          {Array.from(selectedImages).some(filename => 
            images.find(img => img.filename === filename)?.in_use
          ) && (
            <span className="text-xs text-yellow-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Includes in-use images!
            </span>
          )}
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center transition-all
          ${isDragging ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-pink-400'}
          ${uploading ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        <input
          type="file"
          id="file-upload"
          multiple
          accept="image/*"
          onChange={(e) => handleUpload(Array.from(e.target.files))}
          className="hidden"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-pink-500' : 'text-gray-400'}`} />
          <p className="text-lg font-bold mb-1">
            {uploading ? 'Uploading...' : isDragging ? 'Drop images here' : 'Drag & drop images here'}
          </p>
          <p className="text-sm text-gray-500 mb-3">or click to browse</p>
          <Button type="button" variant="outline" disabled={uploading}>
            <Upload className="w-4 h-4 mr-2" />
            Select Files
          </Button>
        </label>
        <p className="text-xs text-gray-400 mt-3">Supports JPG, PNG, GIF, WebP (max 5MB each)</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search images by filename..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:border-pink-500 focus:outline-none"
        />
      </div>

      {/* Image Grid */}
      {filteredImages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Image className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">
            {searchTerm ? 'No images match your search' : 'No images uploaded yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredImages.map((image) => (
            <div
              key={image.filename}
              className={`
                group relative bg-white rounded-lg border-2 overflow-hidden cursor-pointer transition-all
                ${selectMode && selectedImages.has(image.filename) ? 'border-purple-500 ring-2 ring-purple-200' : ''}
                ${!selectMode && selectedImage?.filename === image.filename ? 'border-pink-500 ring-2 ring-pink-200' : 'border-gray-200 hover:border-pink-300'}
              `}
              onClick={() => {
                if (selectMode) {
                  toggleSelectImage(image.filename);
                } else {
                  setSelectedImage(selectedImage?.filename === image.filename ? null : image);
                }
              }}
            >
              {/* In-Use Badge - Always visible when image is in use */}
              {image.in_use && !selectMode && (
                <div className="absolute top-2 left-2 z-10">
                  <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
                    <Check className="w-3 h-3" />
                    IN USE
                  </div>
                </div>
              )}

              {/* Selection checkbox */}
              {selectMode && (
                <div className="absolute top-2 left-2 z-10">
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                    selectedImages.has(image.filename) 
                      ? 'bg-purple-500 border-purple-500' 
                      : 'bg-white/80 border-gray-400'
                  }`}>
                    {selectedImages.has(image.filename) && (
                      <Check className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
              )}
              
              {/* In-Use indicator in select mode */}
              {selectMode && image.in_use && (
                <div className="absolute top-2 right-2 z-10">
                  <div className="bg-green-500 text-white p-1 rounded-full shadow-md" title="In Use - Cannot be safely deleted">
                    <AlertCircle className="w-3 h-3" />
                  </div>
                </div>
              )}

              <div className="aspect-square">
                {isVideo(image.filename) ? (
                  <div className="relative w-full h-full bg-black">
                    <video
                      src={image.url}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-black/50 rounded-full p-2">
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                      </div>
                    </div>
                    <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded pointer-events-none">VIDEO</span>
                  </div>
                ) : (
                  <img
                    src={image.url}
                    alt={image.filename}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="12">Error</text></svg>';
                    }}
                  />
                )}
              </div>
              
              {/* Hover overlay - only show when not in select mode */}
              {!selectMode && (
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(image.url);
                    }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                    title="Copy URL"
                  >
                    {copiedUrl === image.url ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-700" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(image.filename, image.in_use);
                    }}
                    className={`p-2 bg-white rounded-full transition-colors ${
                      image.in_use 
                        ? 'hover:bg-yellow-100' 
                        : 'hover:bg-red-100'
                    }`}
                    title={image.in_use ? "⚠️ Image is in use - delete with caution" : "Delete"}
                  >
                    {image.in_use ? (
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-red-600" />
                    )}
                  </button>
                </div>
              )}

              {/* Filename and status */}
              <div className={`p-2 border-t ${image.in_use ? 'bg-green-50' : 'bg-gray-50'}`}>
                <p className="text-xs font-medium truncate" title={image.filename}>
                  {image.filename}
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400">{formatFileSize(image.size)}</p>
                  {!image.in_use && (
                    <span className="text-xs text-orange-500 font-medium">Unused</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Image Details Panel */}
      {selectedImage && !selectMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border-2 border-pink-200 p-4 flex items-center gap-4 z-50 max-w-lg w-full mx-4">
          {isVideo(selectedImage.filename) ? (
            <video src={selectedImage.url} className="w-16 h-16 rounded-lg object-cover bg-black" muted playsInline preload="metadata" />
          ) : (
            <img
              src={selectedImage.url}
              alt={selectedImage.filename}
              className="w-16 h-16 rounded-lg object-cover"
              referrerPolicy="no-referrer"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{selectedImage.filename}</p>
            <p className="text-sm text-gray-500">
              {formatFileSize(selectedImage.size)} • {formatDate(selectedImage.modified)}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 truncate">
                {selectedImage.url}
              </code>
              <button
                onClick={() => copyToClipboard(selectedImage.url)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                {copiedUrl === selectedImage.url ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          <button
            onClick={() => setSelectedImage(null)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
